import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as RolesIcon } from '../../../assets/icons/sidebar/roles-icon.svg';
import { ReactComponent as ActiveUserIcon } from '../../../assets/icons/user-icon-active.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { ApiRequest } from '../../../interface/common.interface';
import { RequestRoleInterFace, RoleInterFace } from '../../../interface/role-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import {
  disableRolesFilterApplied,
  fetchAllRoles,
  fetchRolesFilters,
  updateRolesFilters,
  setSortData,
} from '../../../redux/slices/RolesManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import RoleSidePanel from '../../../shared-components/table-side-panel/RoleSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import rolesApi from '../../../utils/api/roles';

import AddRole from './add-role';

interface ModalState {
  status: boolean;
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
}

/**
 * User Management page function component
 */
function RolesList() {
  const { t } = useTranslation('userRole');
  const dispatch = useAppDispatch();
  const {
    roles,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.roles);

  const [modalView, setModalView] = useState<ModalState>({
    status: false,
    title: '',
    header: true,
    content: <div />,
    rightCorner: '',
    notification: false,
  });
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [roleToEdit, setRoleToEdit] = useState<RoleInterFace | undefined>(undefined);
  const [addRoleOpen, setAddRoleOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  /**
   * Modal box close function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: true,
      content: <div />,
      rightCorner: '',
      notification: false,
    });
    setAddRoleOpen(false);
    setRoleToEdit(undefined);
  };

  /**
   * Fetch Users list function
   */
  const fetchData = (data: ApiRequest) => {
    if (!loading) {
      dispatch(fetchAllRoles(data));
    }
  };

  /**
   * Handle add action function
   */
  const handleAddAction = () => {
    setAddRoleOpen(true);
  };

  /**
   * Completed the update task
   * @param message
   */
  const completedTask = (message: string) => {
    fetchData({
      page,
      limit,
    });
    setAddRoleOpen(false);
    setRoleToEdit(undefined);
    setModalView({
      status: true,
      title: '',
      header: false,
      content: (
        <SuccessNotification
          title={message}
          description=""
          notificationCloseTimer={3000}
          onNotificationClose={closeModal}
        />
      ),
      rightCorner: '',
      notification: true,
    });
  };

  /**
   * User details view function
   */
  const viewFunction = (currentData: string) => {
    const roleDetail: RoleInterFace | undefined = roles.find(
      (value: RoleInterFace) => value.id === currentData,
    );
    setRoleToEdit(roleDetail);
    setAddRoleOpen(true);
  };
  const column = [
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof RoleInterFace,
      sortAllow: true,
    },
    {
      label: t('roleName'),
      accessorKey: 'name' as keyof RoleInterFace,
      sortAllow: true,
      queryParamsKey: 'roleId',
      sidePanelComponent: <RoleSidePanel fetchIdFrom="queryParams" onEdit={viewFunction} />,
    },
    {
      label: t('responsibility'),
      accessorKey: 'responsibility' as keyof RoleInterFace,
      sortAllow: true,
    },
    // {
    //   label: t('active'),
    //   accessorKey: '' as keyof RoleInterFace,
    //   columnWidth: 60,
    //   customData:
    //     /**
    //      * function for action buttons
    //      * @param currentData
    //      * @returns
    //      */
    //     (currentData: RoleInterFace) => (
    //       <div className="action-button-group">
    //         <div className="device-listing-actions-wrap">
    //           <span role="presentation" onClick={() => viewFunction(currentData.id)}>
    //             <EditPencil />
    //           </span>
    //         </div>
    //       </div>
    //     ),
    // },
  ];

  /**
   * Column definitions for the table
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'createdAt' },
    { label: t('roleName'), accessorKey: 'name' },
    { label: t('responsibility'), accessorKey: 'responsibility' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  const [exportLoader, setExportLoader] = useState<boolean>(false);

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: RequestRoleInterFace) => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: any = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;

    setExportLoader(true);
    rolesApi
      .exportRoles(payload)
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: RoleInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'createdAt':
                return row.createdAt ? ConvertUTCtoDate(row.createdAt) : 'N/A';

              case 'name':
                return row.name || 'N/A';

              case 'responsibility':
                return row.responsibility || 'N/A';

              default:
                return (row as any)[col.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'roles-list.csv');
          } else {
            exportPDF(headers, rows, 'roles-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to open side panel on row click
   */
  const handleOpenSidePanelOnRowClick = (roleId: string) => {
    searchParameters.set('roleId', roleId);
    setSearchParameters(searchParameters);
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    if (isFilterUpdated || roles.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      dispatch(
        fetchAllRoles({
          page: DefaultApiLimit.page,
          limit,
          ...queryParameters,
        }),
      );
      dispatch(disableRolesFilterApplied());
    }
  }, [filters, dispatch, isFilterUpdated, limit, roles.length]);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('roles'),
        icon: <RolesIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults]);

  return (
    <div className="table-content">
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <CustomTable
        dataKey="RoleManagement"
        header={column}
        data={roles}
        serialNo
        version="v2"
        actionButton
        height={window.innerHeight >= 700 ? 332 : 288}
        loading={loading}
        page={page}
        limit={limit}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        onHiddenColumnsChange={setHiddenColumns}
        sizeChangeButtonRequired
        filters={filters}
        filtersToDispatch={updateRolesFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchRolesFilters}
        rowClickEvent={handleOpenSidePanelOnRowClick}
        rowClickParamsKey="roleId"
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportLoader={exportLoader}
        rightTopContent={
          <div className="table-top-button-list">
            <div className="table-button-item">
              <MyButton
                label={t('addRole')}
                buttonType="submit"
                onClickFunc={handleAddAction}
                svgIcon={<ActiveUserIcon />}
              />
            </div>
          </div>
        }
        sidePanelEnabled={addRoleOpen}
        sidePanelManualContent={
          <AddRole
            closeModal={() => setAddRoleOpen(false)}
            roleDetail={roleToEdit}
            completedTask={completedTask}
          />
        }
        onSidePanelClose={() => setAddRoleOpen(false)}
        refreshRequired
        handleRefresh={handleRefresh}
        ReferenceCurrentPages={handleRefresh}
        hoverLabel="Refresh"
      />
    </div>
  );
}

export default RolesList;
